import React, { useState, useCallback, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Typography,
  Box,
  Paper,
  Button,
  LinearProgress,
  Alert,
  AlertTitle,
  Stepper,
  Step,
  StepLabel,
  useMediaQuery,
  useTheme,
  Card,
  CardContent,
  Chip,
  Divider,
  Tooltip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  ListItemSecondaryAction,
  FormGroup,
  FormControlLabel,
  Checkbox,
  Collapse
} from '@mui/material';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import InfoIcon from '@mui/icons-material/Info';
import PauseIcon from '@mui/icons-material/Pause';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import CancelIcon from '@mui/icons-material/Cancel';
import WarningIcon from '@mui/icons-material/Warning';
import DeleteIcon from '@mui/icons-material/Delete';
import InsertDriveFileIcon from '@mui/icons-material/InsertDriveFile';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import DescriptionIcon from '@mui/icons-material/Description';
import HourglassEmptyIcon from '@mui/icons-material/HourglassEmpty';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import SettingsIcon from '@mui/icons-material/Settings';
import { useAuth } from '../contexts/AuthContext';
import api from '../services/api';

// Size constants
const CHUNK_SIZE = 5 * 1024 * 1024; // 5MB
const MAX_FILE_SIZE = 200 * 1024 * 1024; // 200MB

const Upload = () => {
  const { getAccessToken } = useAuth();
  const navigate = useNavigate();
  const [files, setFiles] = useState([]);
  const [currentFile, setCurrentFile] = useState(null); // Currently uploading file
  const [fileQueue, setFileQueue] = useState([]); // Queue of files to upload
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);
  const [activeStep, setActiveStep] = useState(0);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [chunkProgress, setChunkProgress] = useState(0); // Progress within current chunk
  const [uploadStatus, setUploadStatus] = useState('');
  const [uploadPaused, setUploadPaused] = useState(false);
  const [currentChunk, setCurrentChunk] = useState(0);
  const [totalChunks, setTotalChunks] = useState(0);
  const [uploadStartTime, setUploadStartTime] = useState(null);
  const [estimatedTimeRemaining, setEstimatedTimeRemaining] = useState(null);
  const [uploadSpeed, setUploadSpeed] = useState(null);
  const [confirmCancel, setConfirmCancel] = useState(false);
  const [progressAnimation, setProgressAnimation] = useState(''); // Animation style
  const [jobId, setJobId] = useState(null); 
  const [jobStatusPolling, setJobStatusPolling] = useState(null);
  const [completedUploads, setCompletedUploads] = useState([]); // Track completed uploads
  const [showOptions, setShowOptions] = useState(true);
  const [summaryType, setSummaryType] = useState('standard');
  const [usePagesOnly, setUsePagesOnly] = useState(false);
  const [summaryOptions, setSummaryOptions] = useState({
    include_logo: true,
    include_header: true,
    include_abstract: true,
    include_toc: true,
    include_admissions: true,
    include_topics: true,
    include_transcript_ref: true,
    include_disclaimer: true
  });
  const uploadCancelledRef = useRef(false);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const steps = ['Select Files', 'Upload', 'Processing'];

  const formatSpeed = (bytesPerSecond) => {
    if (bytesPerSecond < 1024) {
      return `${bytesPerSecond.toFixed(2)} B/s`;
    } else if (bytesPerSecond < 1024 * 1024) {
      return `${(bytesPerSecond / 1024).toFixed(2)} KB/s`;
    } else {
      return `${(bytesPerSecond / (1024 * 1024)).toFixed(2)} MB/s`;
    }
  };

  const formatTime = (seconds) => {
    if (seconds < 60) {
      return `${Math.ceil(seconds)} seconds`;
    } else if (seconds < 3600) {
      return `${Math.ceil(seconds / 60)} minutes`;
    } else {
      const hours = Math.floor(seconds / 3600);
      const minutes = Math.ceil((seconds % 3600) / 60);
      return `${hours} hours ${minutes} minutes`;
    }
  };

  const handleFileChange = (event) => {
    const selectedFiles = Array.from(event.target.files);
    let validFiles = [];
    let hasError = false;
    
    // Validate all selected files
    for (const selectedFile of selectedFiles) {
      if (selectedFile.type !== 'application/pdf' && selectedFile.type !== 'text/plain') {
        setError(`File "${selectedFile.name}" is not a PDF or text file`);
        hasError = true;
        break;
      }
      
      if (selectedFile.size > MAX_FILE_SIZE) {
        setError(`File "${selectedFile.name}" exceeds ${MAX_FILE_SIZE / (1024 * 1024)}MB limit`);
        hasError = true;
        break;
      }
      
      // Add metadata to each file
      validFiles.push({
        file: selectedFile,
        id: `file-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
        totalChunks: Math.ceil(selectedFile.size / CHUNK_SIZE),
        status: 'pending' // pending, uploading, completed, failed
      });
    }
    
    if (hasError) {
      return;
    }
    
    // Update files list and clear any previous errors
    setFiles(prevFiles => [...prevFiles, ...validFiles]);
    setError(null);
    
    // Move to next step if we have files
    if (validFiles.length > 0) {
      setActiveStep(1);
    }
  };
  
  const removeFile = (fileId) => {
    setFiles(prevFiles => {
      const newFiles = prevFiles.filter(file => file.id !== fileId);
      // If we're removing the last file, reset to step 0
      if (newFiles.length === 0) {
        setActiveStep(0);
      }
      return newFiles;
    });
  };

  const updateProgress = useCallback((progressData) => {
    if (!progressData) return { cancelled: uploadCancelledRef.current };
    
    const { status, progress, currentChunk, totalChunks, chunkProgress: progressWithinChunk } = progressData;
    
    // Set overall progress
    if (progress !== undefined) {
      // Add a smooth transition on progress updates
      setProgressAnimation('all 0.2s ease-in-out');
      setUploadProgress(progress);
    }
    
    // Set progress within the current chunk if available
    if (progressWithinChunk !== undefined) {
      setChunkProgress(progressWithinChunk);
    }
    
    if (status) {
      switch (status) {
        case 'initializing':
          setUploadStatus(currentFile ? `Preparing ${currentFile.file.name} for upload...` : 'Preparing file for upload...');
          break;
        case 'uploading':
          if (currentChunk && totalChunks) {
            if (progressWithinChunk !== undefined) {
              setUploadStatus(`Uploading part ${currentChunk} of ${totalChunks} (${progressWithinChunk}%)`);
            } else {
              setUploadStatus(`Uploading part ${currentChunk} of ${totalChunks}`);
            }
            setCurrentChunk(currentChunk);
          }
          break;
        case 'finalizing':
          setUploadStatus('Finalizing upload...');
          break;
        case 'processing':
          setUploadStatus('Processing file...');
          break;
        case 'completed':
          setUploadStatus(currentFile ? `Upload of ${currentFile.file.name} complete!` : 'Upload complete!');
          break;
        case 'error':
          setError(progressData.error || 'An error occurred during upload');
          break;
        default:
          setUploadStatus(status);
      }
    }
    
    // Calculate upload speed and estimated time
    if (status === 'uploading' && uploadStartTime && currentChunk && currentFile) {
      const elapsedSeconds = (Date.now() - uploadStartTime) / 1000;
      if (elapsedSeconds > 0) {
        // Calculate bytes uploaded including current chunk progress
        let bytesUploaded = ((currentChunk - 1) * CHUNK_SIZE);
        if (progressWithinChunk !== undefined) {
          // Add partial progress of current chunk
          const currentChunkSize = Math.min(CHUNK_SIZE, currentFile.file.size - ((currentChunk - 1) * CHUNK_SIZE));
          bytesUploaded += (currentChunkSize * (progressWithinChunk / 100));
        }
        
        const bytesPerSecond = bytesUploaded / elapsedSeconds;
        
        if (bytesPerSecond > 0) {
          const bytesRemaining = currentFile.file.size - bytesUploaded;
          const secondsRemaining = bytesRemaining / bytesPerSecond;
          
          setUploadSpeed(formatSpeed(bytesPerSecond));
          setEstimatedTimeRemaining(formatTime(secondsRemaining));
        }
      }
    }
    
    return { cancelled: uploadCancelledRef.current };
  }, [uploadStartTime, currentFile]);

  const handleCancelConfirm = () => {
    setConfirmCancel(true);
  };

  const handleCancelUpload = () => {
    uploadCancelledRef.current = true;
    setConfirmCancel(false);
    setLoading(false);
    setActiveStep(1);
    setUploadStatus('Upload cancelled');
    setError('Upload was cancelled');
  };

  const handleCancelDialog = () => {
    setConfirmCancel(false);
  };

  const togglePause = () => {
    setUploadPaused(!uploadPaused);
    setUploadStatus(uploadPaused ? 'Resuming upload...' : 'Upload paused');
  };

  const handleOptionChange = (event) => {
    setSummaryOptions({
      ...summaryOptions,
      [event.target.name]: event.target.checked
    });
  };

  const optionLabels = {
    include_logo: 'Include Logo',
    include_header: 'Include Header',
    include_abstract: 'Include Abstract',
    include_toc: 'Include Table of Contents',
    include_admissions: 'Include Admissions',
    include_topics: 'Include Topics',
    include_transcript_ref: 'Include Transcript References',
    include_disclaimer: 'Include Disclaimer'
  };

  // Poll job status periodically
  const pollJobStatus = useCallback(async (jobId) => {
    console.log(`Starting to poll job status for: ${jobId}`);
    
    // Clear any existing polling interval
    if (jobStatusPolling) {
      console.log("Clearing existing polling interval");
      clearInterval(jobStatusPolling);
      setJobStatusPolling(null);
    }
    
    // Poll job status every 2 seconds
    const intervalId = setInterval(async () => {
      try {
        console.log(`Polling job status for: ${jobId}`);
        
        const token = await getAccessToken();
        const jobStatus = await api.getJobStatus(jobId, token);
        
        if (jobStatus.found) {
          if (jobStatus.status === 'processing') {
            setUploadStatus(`Processing job ${jobId}...`);
          } else if (jobStatus.status === 'completed') {
            // Job completed successfully
            setUploadStatus('Processing complete!');
            setSuccess(true);
            clearInterval(intervalId);
            setJobStatusPolling(null);
            
            // Navigate to jobs page after a short delay
            setTimeout(() => {
              navigate('/jobs');
            }, 2000);
          } else if (jobStatus.status === 'failed') {
            // Job failed
            setError(`Processing failed: ${jobStatus.error || 'Unknown error'}`);
            clearInterval(intervalId);
            setJobStatusPolling(null);
            setLoading(false);
          }
        } else {
          // Job not found
          console.warn(`Job ${jobId} not found`);
          // Don't stop polling immediately, might be a temporary issue
          // Just log a warning instead
        }
      } catch (err) {
        console.error('Error polling job status:', err);
        // Don't stop polling on temporary errors
      }
    }, 2000);
    
    // Store the interval ID
    setJobStatusPolling(intervalId);
  }, [getAccessToken, navigate, jobStatusPolling, setUploadStatus, setSuccess, setError, setLoading, setJobStatusPolling]);

  // Clean up polling interval when component unmounts
  useEffect(() => {
    return () => {
      if (jobStatusPolling) {
        clearInterval(jobStatusPolling);
      }
    };
  }, [jobStatusPolling]);
  
  // Prepare next file for processing when current file completes
  useEffect(() => {
    // Initialize the queue if we have files but no queue
    if (files.length > 0 && files.some(f => f.status === 'pending') && fileQueue.length === 0 && !loading) {
      // This effect will only start the initial queue
      // Further queue processing is handled by processNextFile
    }
  }, [files, fileQueue.length, loading]);
  
  // Process the next file in the queue - using a ref to prevent multiple simultaneous processing
  const isProcessingRef = useRef(false);
  
  const processNextFile = useCallback(async () => {
    // If we're already processing, don't start another process
    if (isProcessingRef.current) {
      console.log('Already processing a file, skipping');
      return;
    }
    
    console.log('Processing next file, queue length:', fileQueue.length);
    
    // Check if there are files to process
    if (fileQueue.length === 0) {
      console.log('No more files in queue to process');
      setLoading(false);
      isProcessingRef.current = false;
      return;
    }
    
    // Mark that we're processing
    isProcessingRef.current = true;
    
    // Get the next file from the queue
    const nextFile = fileQueue[0];
    console.log(`Starting upload of ${nextFile.file.name}`);
    
    // Update states
    setCurrentFile(nextFile);
    
    // Remove the current file from the queue
    const newQueue = fileQueue.slice(1);
    console.log(`Removing file from queue. New queue length: ${newQueue.length}`);
    setFileQueue(newQueue);
    
    // Update file status
    setFiles(prevFiles => prevFiles.map(file => 
      file.id === nextFile.id ? { ...file, status: 'uploading' } : file
    ));
    
    // Reset progress indicators for this file
    setUploadProgress(0);
    setChunkProgress(0);
    setTotalChunks(nextFile.totalChunks);
    setUploadStatus(`Initializing upload for ${nextFile.file.name}...`);
    setUploadStartTime(Date.now());
    setUploadPaused(false);
    uploadCancelledRef.current = false;
    
    try {
      const token = await getAccessToken();
      
      // Upload file with progress tracking
      const result = await api.uploadFile(nextFile.file, token, updateProgress, { ...summaryOptions, summary_type: summaryType, use_pages_only: usePagesOnly });
      console.log(`Completed upload of ${nextFile.file.name}, result:`, result);
      
      // Update file status to completed
      setFiles(prevFiles => prevFiles.map(file => 
        file.id === nextFile.id ? { ...file, status: 'completed', jobId: result?.jobId } : file
      ));
      
      // Add to completed uploads
      setCompletedUploads(prev => {
        // Avoid duplicate entries
        if (!prev.some(item => item.id === nextFile.id)) {
          return [...prev, { 
            id: nextFile.id, 
            fileName: nextFile.file.name,
            jobId: result?.jobId 
          }];
        }
        return prev;
      });
      
      // If the API returned a job ID, add it to the tracking
      if (result && result.jobId) {
        console.log(`File ${nextFile.file.name} uploaded with job ID: ${result.jobId}`);
        setJobId(result.jobId);
        
        // For files that need background processing (have a job ID)
        setUploadStatus(`Upload complete for ${nextFile.file.name}. Processing in background...`);
        
        // For the last file, or when there are no more files in the queue, 
        // we want to poll its status to show the final processing
        if (fileQueue.length === 0) {
          console.log(`Starting to poll status for last file's job: ${result.jobId}`);
          pollJobStatus(result.jobId);
        }
      } else {
        console.log(`File ${nextFile.file.name} was processed directly without a job ID`);
        // This is a small file that was processed directly without a job ID
        setUploadStatus(`Upload of ${nextFile.file.name} complete!`);
        
        // If this is the last file and no job ID was returned, we should mark as success
        // but we'll navigate in the fileQueue check after this
      }
      
      // Reset processing flag
      isProcessingRef.current = false;
      
      // We need to check if there are more files to process
      // React state updates are asynchronous, so we can't rely on fileQueue
      // Check against the remaining file count directly
      const remainingFiles = fileQueue.length;
      console.log(`Remaining files to process: ${remainingFiles}`);
      
      // Check if this was the last file (if no more files to process)
      if (remainingFiles === 0) {
        console.log('This was the last file in the queue - setting success flag');
        // Mark as success but let the useEffect handle navigation
        setSuccess(true);
      } else {
        // Not the last file, continue processing the queue
        console.log(`${remainingFiles} files remaining in queue, continuing`);
        // Don't call processNextFile directly - use a state update to trigger the useEffect
        setFileQueue(prev => [...prev]); // trigger re-render without changing data
      }
    } catch (err) {
      if (!uploadCancelledRef.current) {
        console.error('Upload error:', err);
        
        // Update file status to failed
        setFiles(prevFiles => prevFiles.map(file => 
          file.id === nextFile.id ? { ...file, status: 'failed', error: err.message } : file
        ));
        
        setError(`Failed to upload ${nextFile.file.name}: ${err.response?.data?.message || err.message}`);
        
        // Reset processing flag
        isProcessingRef.current = false;
        
        // Check remaining files directly
        const remainingFiles = fileQueue.length;
        console.log(`Remaining files after failure: ${remainingFiles}`);
        
        // Check if this was the last file that failed
        if (remainingFiles === 0) {
          console.log('Last file in the queue failed');
          // If we had success with other files, mark partial success
          if (completedUploads.length > 0) {
            setSuccess(true);
          }
        } else {
          // Not the last file, continue processing the queue
          console.log(`File failed but ${remainingFiles} files remaining in queue, continuing`);
          // Don't call processNextFile directly
          setFileQueue(prev => [...prev]); // trigger re-render without changing data
        }
      }
    }
  }, [fileQueue, updateProgress, getAccessToken, pollJobStatus, navigate, completedUploads, uploadCancelledRef, jobStatusPolling, summaryOptions]);
  
  // Process next file whenever the queue changes
  useEffect(() => {
    // Only process queue if we're actually in upload mode (loading is true)
    // and if we have files in the queue and aren't already processing
    if (loading && fileQueue.length > 0 && !isProcessingRef.current) {
      console.log(`Queue changed - length: ${fileQueue.length}, processing next file`);
      
      // Use a slight timeout to ensure state updates have completed
      const timeoutId = setTimeout(() => {
        processNextFile();
      }, 50);
      
      // Clean up timeout if component unmounts
      return () => clearTimeout(timeoutId);
    }
  }, [fileQueue, processNextFile, loading]);
  
  // Handle final navigation after all uploads are done
  useEffect(() => {
    // Navigate to jobs page once all uploads are complete
    if (loading && completedUploads.length > 0 && fileQueue.length === 0 && !isProcessingRef.current) {
      console.log('All uploads complete, checking for navigation');
      
      // If we're not polling a job, navigate to jobs page
      if (!jobStatusPolling) {
        console.log('No active polling, navigating to jobs page');
        setSuccess(true);
        const timeoutId = setTimeout(() => {
          console.log('Navigating to jobs page now');
          navigate('/jobs');
        }, 2000);
        
        return () => clearTimeout(timeoutId);
      }
    }
  }, [loading, completedUploads, fileQueue, isProcessingRef, jobStatusPolling, navigate]);
  
  const handleUpload = () => {
    if (files.length === 0) {
      setError('Please select at least one file first');
      return;
    }
    
    if (files.every(f => f.status !== 'pending')) {
      setError('No pending files to upload');
      return;
    }
    
    // Reset any existing state
    setCompletedUploads([]);
    setLoading(true);
    setError(null);
    setActiveStep(2);
    setCurrentFile(null);
    isProcessingRef.current = false;
    
    // Filter only pending files and create a fresh queue
    const pendingFiles = files.filter(file => file.status === 'pending');
    console.log(`Queueing ${pendingFiles.length} files for upload`);
    
    // Queue all pending files - the useEffect will trigger processing
    setFileQueue(pendingFiles);
  };

  return (
    <Box sx={{ px: { xs: 1, sm: 2, md: 0 } }}>
      <Typography 
        variant={isMobile ? "h5" : "h4"} 
        component="h1" 
        mb={3}
        sx={{ 
          textAlign: { xs: 'center', sm: 'left' },
          mt: { xs: 1, sm: 2 }
        }}
      >
        Upload File(s) for Summarization
      </Typography>

      <Paper sx={{ p: { xs: 2, sm: 3 } }}>
        <Stepper 
          activeStep={activeStep} 
          alternativeLabel 
          sx={{ 
            mb: { xs: 3, sm: 4 },
            '& .MuiStepLabel-label': {
              fontSize: isMobile ? '0.8rem' : '0.875rem'
            }
          }}
        >
          {steps.map((label) => (
            <Step key={label}>
              <StepLabel>{label}</StepLabel>
            </Step>
          ))}
        </Stepper>

        {error && (
          <Alert severity="error" sx={{ mb: 3 }}>
            <AlertTitle>Error</AlertTitle>
            {error}
          </Alert>
        )}

        {success ? (
          <Box display="flex" flexDirection="column" alignItems="center" my={3}>
            <CheckCircleIcon color="success" sx={{ fontSize: { xs: 40, sm: 60 }, mb: 2 }} />
            <Typography 
              variant={isMobile ? "subtitle1" : "h6"} 
              color="success.main"
              align="center"
            >
              File successfully uploaded for summarization
            </Typography>
            <Typography color="textSecondary" align="center" sx={{ mt: 1 }}>
              Redirecting to jobs page...
            </Typography>
          </Box>
        ) : (
          <Box>
            <Box
              sx={{
                border: '2px dashed #ccc',
                borderRadius: 2,
                p: { xs: 3, sm: 5 },
                textAlign: 'center',
                mb: 3,
                backgroundColor: '#f9f9f9'
              }}
            >
              <input
                type="file"
                accept="application/pdf,text/plain"
                id="file-upload"
                style={{ display: 'none' }}
                onChange={handleFileChange}
                disabled={loading}
                multiple
              />
              <label htmlFor="file-upload">
                <Button
                  variant="contained"
                  component="span"
                  startIcon={<CloudUploadIcon />}
                  disabled={loading}
                  size={isMobile ? "medium" : "large"}
                  sx={{ mb: 2 }}
                >
                  Select Files
                </Button>
              </label>
              
              <Typography 
                variant="body2" 
                color="text.secondary"
                sx={{ mt: 1 }}
              >
                Supported formats: PDF, TXT. Maximum file size: 200MB per file
              </Typography>
              
              {files.length > 0 && (
                <Box mt={3}>
                  <Typography 
                    variant="body2" 
                    fontWeight="medium" 
                    sx={{ mb: 1, textAlign: 'left' }}
                  >
                    Selected files ({files.length}):
                  </Typography>
                  
                  <List 
                    sx={{ 
                      bgcolor: 'background.paper',
                      border: '1px solid #eee',
                      borderRadius: 1,
                      maxHeight: '250px',
                      overflow: 'auto'
                    }}
                  >
                    {files.map((fileItem) => {
                      const fileIcon = fileItem.file.type === 'application/pdf' 
                        ? <PictureAsPdfIcon color="error" />
                        : <DescriptionIcon color="primary" />;
                      
                      let statusChip;
                      if (fileItem.status === 'pending') {
                        statusChip = <Chip label="Pending" size="small" variant="outlined" />;
                      } else if (fileItem.status === 'uploading') {
                        statusChip = <Chip label="Uploading" size="small" color="primary" variant="outlined" />;
                      } else if (fileItem.status === 'completed') {
                        statusChip = <Chip label="Completed" size="small" color="success" />;
                      } else if (fileItem.status === 'failed') {
                        statusChip = <Chip label="Failed" size="small" color="error" />;
                      }
                      
                      return (
                        <ListItem key={fileItem.id}>
                          <ListItemIcon>
                            {fileIcon}
                          </ListItemIcon>
                          <ListItemText 
                            primary={fileItem.file.name}
                            secondary={`${(fileItem.file.size / 1024 / 1024).toFixed(2)} MB${
                              fileItem.totalChunks > 1 ? ` • ${fileItem.totalChunks} chunks` : ''
                            }`}
                            primaryTypographyProps={{
                              style: {
                                whiteSpace: 'nowrap',
                                overflow: 'hidden',
                                textOverflow: 'ellipsis'
                              }
                            }}
                          />
                          <ListItemSecondaryAction sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            {statusChip}
                            {fileItem.status === 'pending' && (
                              <IconButton 
                                edge="end" 
                                aria-label="delete" 
                                size="small"
                                onClick={() => removeFile(fileItem.id)}
                              >
                                <DeleteIcon fontSize="small" />
                              </IconButton>
                            )}
                          </ListItemSecondaryAction>
                        </ListItem>
                      );
                    })}
                  </List>
                </Box>
              )}
            </Box>

            {/* Upload Button - Always visible at top */}
            <Box display="flex" justifyContent="center" sx={{ mb: 3 }}>
              <Button
                variant="contained"
                color="primary"
                onClick={handleUpload}
                disabled={files.length === 0 || loading || files.every(f => f.status !== 'pending')}
                sx={{
                  minWidth: { xs: '100%', sm: 200 },
                  py: { xs: 1.5, sm: 1 }
                }}
                size={isMobile ? "medium" : "large"}
              >
                {loading ? 'Uploading...' : 'Upload and Process'}
              </Button>
            </Box>

            {/* Summary Options - Always visible */}
            {!loading && (
              <Box sx={{ mb: 3 }}>
                <Button
                  onClick={() => setShowOptions(!showOptions)}
                  startIcon={<SettingsIcon />}
                  endIcon={showOptions ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                  sx={{ mb: 1 }}
                >
                  Summary Options
                </Button>
                <Collapse in={showOptions}>
                  <Paper variant="outlined" sx={{ p: 2 }}>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                      Select the type of summary to generate:
                    </Typography>
                    <FormGroup>
                      {/* Summary Type: Standard Summary */}
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={summaryType === 'standard'}
                            onChange={() => { setSummaryType('standard'); setUsePagesOnly(false); }}
                            size="small"
                          />
                        }
                        label="Standard Summary"
                      />
                      {/* Standard Summary sub-options */}
                      <Collapse in={summaryType === 'standard'}>
                        <Box sx={{
                          display: 'grid',
                          gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' },
                          gap: 1,
                          ml: 3,
                          mt: 1,
                          mb: 1
                        }}>
                          {Object.entries(summaryOptions).map(([key, value]) => (
                            <FormControlLabel
                              key={key}
                              control={
                                <Checkbox
                                  checked={value}
                                  onChange={handleOptionChange}
                                  name={key}
                                  size="small"
                                />
                              }
                              label={optionLabels[key]}
                            />
                          ))}
                        </Box>
                      </Collapse>
                      {/* Summary Type: 2 Page Summary */}
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={summaryType === '2page'}
                            onChange={() => { setSummaryType('2page'); setUsePagesOnly(false); }}
                            size="small"
                          />
                        }
                        label="2 Page Summary"
                      />
                      {/* Summary Type: Page Line Summary */}
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={summaryType === 'pageline'}
                            onChange={() => setSummaryType('pageline')}
                            size="small"
                          />
                        }
                        label="Page Line Summary"
                      />
                      <Collapse in={summaryType === 'pageline'}>
                        <Box sx={{ ml: 3, mt: 0.5, mb: 0.5 }}>
                          <FormControlLabel
                            control={
                              <Checkbox
                                checked={!usePagesOnly}
                                onChange={() => setUsePagesOnly(false)}
                                size="small"
                              />
                            }
                            label="Summarize Every Few Pages"
                          />
                          <FormControlLabel
                            control={
                              <Checkbox
                                checked={usePagesOnly}
                                onChange={() => setUsePagesOnly(true)}
                                size="small"
                              />
                            }
                            label="Summarize Each Page"
                          />
                        </Box>
                      </Collapse>
                    </FormGroup>
                  </Paper>
                </Collapse>
              </Box>
            )}

            {loading && (
              <Card variant="outlined" sx={{ mb: 3 }}>
                <CardContent>
                  <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
                    <Box>
                      <Typography variant="body2" fontWeight="medium">{uploadStatus}</Typography>
                      {(currentFile || fileQueue.length > 0) && (
                        <Typography variant="caption" color="text.secondary">
                          {completedUploads.length} of {files.filter(f => f.status !== 'failed').length} files completed
                        </Typography>
                      )}
                    </Box>
                    <Box display="flex" alignItems="center">
                      {currentFile && totalChunks > 1 && uploadProgress < 100 && (
                        <>
                          <IconButton 
                            size="small" 
                            onClick={togglePause}
                            sx={{ mr: 1 }}
                            disabled={uploadProgress >= 100}
                          >
                            {uploadPaused ? <PlayArrowIcon /> : <PauseIcon />}
                          </IconButton>
                          <IconButton 
                            size="small" 
                            onClick={handleCancelConfirm}
                            sx={{ mr: 1 }}
                            color="error"
                            disabled={uploadProgress >= 100}
                          >
                            <CancelIcon />
                          </IconButton>
                        </>
                      )}
                      {jobId ? (
                        <Chip 
                          color="primary" 
                          size="small"
                          label="Processing in background" 
                          variant="outlined"
                          sx={{ fontSize: '0.75rem' }}
                        />
                      ) : (
                        <Typography variant="body2" fontWeight="medium">{uploadProgress}%</Typography>
                      )}
                    </Box>
                  </Box>
                  
                  {/* Main progress bar */}
                  <LinearProgress 
                    variant={jobId ? "indeterminate" : "determinate"} 
                    value={uploadProgress} 
                    sx={{ 
                      height: 10, 
                      borderRadius: 5, 
                      mb: 1,
                      transition: progressAnimation
                    }}
                    color={jobId ? "secondary" : "primary"}
                  />
                  
                  {/* Current file indicator */}
                  {currentFile && (
                    <Typography variant="body2" color="text.secondary" sx={{ my: 1 }}>
                      Current file: <strong>{currentFile.file.name}</strong>
                    </Typography>
                  )}
                  
                  {/* Chunk progress bar (visible only when uploading chunks and not in background processing) */}
                  {currentFile && totalChunks > 1 && currentChunk > 0 && !jobId && (
                    <Box>
                      <Box display="flex" justifyContent="space-between" mb={0.5}>
                        <Typography variant="caption" color="text.secondary">
                          Current chunk progress
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {chunkProgress}%
                        </Typography>
                      </Box>
                      <LinearProgress 
                        variant="determinate" 
                        value={chunkProgress} 
                        sx={{ 
                          height: 6, 
                          borderRadius: 3, 
                          mb: 2,
                          transition: 'all 0.1s linear',
                          opacity: 0.7
                        }}
                        color="secondary"
                      />
                    </Box>
                  )}
                  
                  <Divider sx={{ my: 1 }} />
                  
                  {fileQueue.length === 0 && completedUploads.length > 0 ? (
                    <Box 
                      display="flex" 
                      flexDirection="column"
                      mt={1}
                      gap={1}
                    >
                      <Typography variant="body2" color="text.secondary" fontStyle="italic">
                        All files uploaded. {jobStatusPolling ? "Files are being processed in the background." : ""} You can safely navigate away from this page.
                      </Typography>
                      {jobId && (
                        <Typography variant="body2" color="text.secondary">
                          Latest Job ID: {jobId}
                        </Typography>
                      )}
                      <Typography variant="body2" color="text.secondary">
                        You'll be redirected to the jobs page when all processing completes.
                      </Typography>
                    </Box>
                  ) : (
                    <Box 
                      display="flex" 
                      flexDirection={{ xs: 'column', sm: 'row' }} 
                      justifyContent="space-between"
                      mt={1}
                    >
                      {fileQueue.length > 0 && (
                        <Typography variant="body2" color="text.secondary">
                          {completedUploads.length} of {completedUploads.length + fileQueue.length + (currentFile ? 1 : 0)} files completed
                        </Typography>
                      )}
                    
                      {totalChunks > 1 && currentChunk > 0 && (
                        <Typography variant="body2" color="text.secondary">
                          Chunk {currentChunk} of {totalChunks}
                        </Typography>
                      )}
                      
                      {uploadSpeed && (
                        <Typography variant="body2" color="text.secondary">
                          Upload speed: {uploadSpeed}
                        </Typography>
                      )}
                      
                      {estimatedTimeRemaining && uploadProgress < 100 && (
                        <Typography variant="body2" color="text.secondary">
                          Estimated time remaining: {estimatedTimeRemaining}
                        </Typography>
                      )}
                    </Box>
                  )}
                </CardContent>
              </Card>
            )}
          </Box>
        )}
      </Paper>
      
      {/* Cancel Confirmation Dialog */}
      <Dialog
        open={confirmCancel}
        onClose={handleCancelDialog}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
      >
        <DialogTitle id="alert-dialog-title">
          <Box display="flex" alignItems="center">
            <WarningIcon color="warning" sx={{ mr: 1 }} />
            Cancel Upload?
          </Box>
        </DialogTitle>
        <DialogContent>
          <DialogContentText id="alert-dialog-description">
            Are you sure you want to cancel this upload? Your progress will be lost.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCancelDialog} color="primary">
            Continue Uploading
          </Button>
          <Button onClick={handleCancelUpload} color="error" autoFocus>
            Cancel Upload
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Upload;